/**
 * Central ReHub credit-cost catalogue.
 *
 * Apps must call getCreditCost({ app, feature }) instead of hard-coding
 * generation prices. Provider / model overrides are optional and never
 * change Stripe checkout amounts.
 */

import { HIOZ_CREDIT_COSTS } from './hioz-plans';
import {
  PROPERTYCGI_CREDIT_COST_1K,
  PROPERTYCGI_CREDIT_COST_2K,
  PROPERTYCGI_CREDIT_COST_4K,
  PROPERTYCGI_CREDIT_COST_EDIT,
} from './propertycgi';
import {
  formatVideomotionCredits,
  VIDEOMOTION_CREDITS_FOR_ANALYSIS,
  VIDEOMOTION_CREDITS_FOR_EXPORT,
  VIDEOMOTION_CREDITS_FOR_SCRIPT,
  VIDEOMOTION_MAX_VIDEOS_PER_PROJECT,
} from './videomotion';
import {
  PROPERTYCUT_IMAGEMOTION_SCENE_CREDITS,
  PROPERTYCUT_VIDEOMOTION_WALKTHROUGH_CREDITS,
} from './propertycut';
import { SEO_ACTION_CREDITS } from './seo-plans';

/** Keep in sync with HDR_CREDIT_COST / brochure / hosting constants in index.ts. */
const HDR_CREDIT_COST = 0;
/** Keep equal to BROCHURE_GENERATE_CREDITS in index.ts. Branded PDFs are free. */
const BROCHURE_GENERATE_CREDITS = 0;
/** Keep equal to BROCHURE_UNBRANDED_CREDITS in index.ts. */
const BROCHURE_UNBRANDED_CREDITS = 1;
/** Keep equal to BROCHURE_AI_STYLE_CREDITS in index.ts. */
const BROCHURE_AI_STYLE_CREDITS = 2;
const BROCHURE_MARKETING_PACK_CREDITS = 2;
const TOURS_FLOOR_PLAN_ORDER_CREDITS = 20;
/** Keep equal to TOURS_PANO_EDIT_CREDITS in index.ts and tours_pano_edit_credit_cost(). */
const TOURS_PANO_EDIT_CREDITS = 1;
const HOSTING_CREDIT_COSTS: { feature: string; credits: number; label: string }[] = [
  { feature: '7d', credits: 2, label: '7 Days' },
  { feature: '30d', credits: 3, label: '30 Days' },
  { feature: '12mo', credits: 10, label: '1 Year' },
  { feature: '6mo', credits: 6, label: '6 Months' },
];
const SHOWCASE_HOSTING_CREDIT_COSTS: { feature: string; credits: number; label: string }[] = [
  { feature: 'hosting_7d', credits: 3, label: '7 Days' },
  { feature: 'hosting_30d', credits: 8, label: '30 Days' },
  { feature: 'hosting_6mo', credits: 18, label: '6 Months' },
  { feature: 'hosting_12mo', credits: 25, label: '12 Months' },
];

export interface CreditActionKey {
  app: string;
  feature: string;
  provider?: string | null;
  model?: string | null;
}

export interface CreditActionDefinition {
  app: string;
  feature: string;
  creditCost: number;
  label: string;
  buttonLabel: string;
  provider?: string;
  model?: string;
  enabled?: boolean;
}

export const PHOTOCLEAR_PRICING_VERSION = 'photoclear-actions-v1';

export interface PhotoclearOperationPrice {
  operation: string;
  feature: string;
  creditCost: number;
  label: string;
}

/** One row per customer-requested PhotoClear generation. Presets of the same tool share a feature. */
export const PHOTOCLEAR_OPERATIONS: readonly PhotoclearOperationPrice[] = [
  { operation: 'photoclear_declutter_light', feature: 'declutter_light', creditCost: 1, label: 'Light declutter' },
  { operation: 'photoclear_declutter_medium', feature: 'declutter_medium', creditCost: 1, label: 'Medium declutter' },
  { operation: 'photoclear_auto', feature: 'declutter_medium', creditCost: 1, label: 'Medium declutter' },
  { operation: 'photoclear_declutter_full', feature: 'declutter_full', creditCost: 1, label: 'Full declutter' },
  { operation: 'photoclear_remove_selection', feature: 'remove_selection', creditCost: 1, label: 'Remove selection' },
  { operation: 'photoclear_custom', feature: 'custom', creditCost: 1, label: 'Custom edit' },
  { operation: 'photoclear_bed_straighten', feature: 'straighten_bed', creditCost: 1, label: 'Straighten bed' },
  { operation: 'photoclear_sofa_straighten', feature: 'straighten_sofa', creditCost: 1, label: 'Straighten sofa' },
  { operation: 'photoclear_garden_declutter', feature: 'tidy_garden', creditCost: 1, label: 'Tidy garden' },
  { operation: 'photoclear_cut_grass', feature: 'cut_grass', creditCost: 1, label: 'Cut the grass' },
  { operation: 'photoclear_driveway_declutter', feature: 'tidy_drive', creditCost: 1, label: 'Tidy drive and bins' },
  { operation: 'photoclear_bins_only', feature: 'remove_bins', creditCost: 1, label: 'Remove bins only' },
  { operation: 'photoclear_cars_only', feature: 'remove_cars', creditCost: 1, label: 'Remove cars only' },
  { operation: 'photoclear_brighten_enhance', feature: 'brighten_enhance', creditCost: 1, label: 'Brighten and enhance' },
  { operation: 'photoclear_turn_on_lights', feature: 'turn_on_lights', creditCost: 1, label: 'Turn on lights' },
  { operation: 'photoclear_blur_personal_photos', feature: 'blur_personal_photos', creditCost: 1, label: 'Blur personal photos' },
  { operation: 'photoclear_blur_license_plates', feature: 'blur_license_plates', creditCost: 1, label: 'Blur license plates' },
  { operation: 'photoclear_remove_shadows', feature: 'remove_shadows', creditCost: 1, label: 'Remove shadows' },
  { operation: 'photoclear_blue_sky', feature: 'blue_sky', creditCost: 1, label: 'Blue sky' },
  { operation: 'photoclear_sunset_sky', feature: 'sunset_sky', creditCost: 1, label: 'Sunset sky' },
  { operation: 'photoclear_straighten_image', feature: 'straighten_image', creditCost: 1, label: 'Straighten image' },
  { operation: 'photoclear_window_sky', feature: 'window_sky', creditCost: 1, label: 'Window sky' },
  { operation: 'photoclear_add_flowers', feature: 'add_flowers', creditCost: 1, label: 'Add flowers' },
  { operation: 'photoclear_add_plants', feature: 'add_plants', creditCost: 1, label: 'Add plant' },
  { operation: 'photoclear_upscale', feature: 'upscale', creditCost: 1, label: 'Upscale' },
];

const PHOTOCLEAR_BY_OPERATION = new Map(PHOTOCLEAR_OPERATIONS.map((row) => [row.operation, row]));

export function formatPhotoclearCreditLabel(credits: number): string {
  const amount = Number(credits);
  return amount === 1 ? '1 credit' : `${amount} credits`;
}

export function quotePhotoclearOperation(operation: string): PhotoclearOperationPrice | null {
  return PHOTOCLEAR_BY_OPERATION.get(operation) ?? null;
}

export function photoclearFeatureForOperation(operation: string): string | null {
  return quotePhotoclearOperation(operation)?.feature ?? null;
}

/**
 * Server and client both use this rule: unknown actions are rejected, and a
 * displayed quote that no longer matches the live price is not charged.
 */
export function resolvePhotoclearCharge(input: {
  operation: string;
  quotedCost: number;
  liveCost?: number | null;
}):
  | { ok: true; feature: string; creditCost: number; label: string }
  | { ok: false; error: 'UNKNOWN_ACTION' | 'STALE_QUOTE'; creditCost?: number; label?: string } {
  const action = quotePhotoclearOperation(input.operation);
  if (!action) return { ok: false, error: 'UNKNOWN_ACTION' };
  const live = input.liveCost == null ? action.creditCost : Number(input.liveCost);
  if (Number(input.quotedCost) !== live) {
    return { ok: false, error: 'STALE_QUOTE', creditCost: live, label: action.label };
  }
  return { ok: true, feature: action.feature, creditCost: live, label: action.label };
}

const ACTIONS: CreditActionDefinition[] = [
  {
    app: 'photoclear',
    feature: 'declutter',
    creditCost: 1,
    label: 'PhotoClear / light declutter',
    buttonLabel: 'Light declutter — 1 credit',
  },
  {
    app: 'photoedit',
    feature: 'standard_enhancement',
    creditCost: 1,
    label: 'Standard enhancement',
    buttonLabel: 'Enhance — 1 credit',
  },
  {
    app: 'hdrphoto',
    feature: 'merge',
    creditCost: HDR_CREDIT_COST,
    label: 'HDRPhoto',
    buttonLabel: 'Process HDR image',
  },
  {
    app: 'hyperdusk',
    feature: 'day_to_dusk',
    creditCost: 1,
    label: 'HyperDusk / day-to-dusk',
    buttonLabel: 'Generate — 1 credit',
  },
  {
    app: 'floorplans-studio',
    feature: 'ai_conversion',
    creditCost: 5,
    label: 'Floorplans Studio / AI sketch conversion (typical)',
    buttonLabel: 'Quoted after analysis',
  },
  {
    app: 'staging',
    feature: 'standard',
    creditCost: 3,
    label: 'Virtual Staging Standard',
    buttonLabel: 'Generate — 3 credits',
  },
  {
    app: 'staging',
    feature: 'premium',
    creditCost: 5,
    label: 'Virtual Staging Premium',
    buttonLabel: 'Premium — 5 credits',
  },
  {
    app: 'staging',
    feature: 'full_renovation',
    creditCost: 10,
    label: 'Virtual Staging Full Renovation',
    buttonLabel: 'Full renovation — 10 credits',
  },
  {
    app: 'imagemotion',
    feature: 'quick',
    creditCost: 12,
    label: 'ImageMotion Quick',
    buttonLabel: 'Animate — 12 credits',
  },
  {
    app: 'imagemotion',
    feature: 'standard',
    creditCost: 22,
    label: 'ImageMotion Standard',
    buttonLabel: 'Animate — 22 credits',
  },
  {
    app: 'imagemotion',
    feature: 'pro',
    creditCost: 32,
    label: 'ImageMotion Pro',
    buttonLabel: 'Premium — 32 credits',
  },
  {
    app: 'imagemotion',
    feature: 'ultra',
    creditCost: 43,
    label: 'ImageMotion Ultra/Master',
    buttonLabel: 'Ultra — 43 credits',
  },
  {
    app: 'propertycgi',
    feature: '1k',
    creditCost: PROPERTYCGI_CREDIT_COST_1K,
    label: 'PropertyCGI 1K',
    buttonLabel: `Generate — ${PROPERTYCGI_CREDIT_COST_1K} credits`,
  },
  {
    app: 'propertycgi',
    feature: '2k',
    creditCost: PROPERTYCGI_CREDIT_COST_2K,
    label: 'PropertyCGI 2K',
    buttonLabel: `Generate — ${PROPERTYCGI_CREDIT_COST_2K} credits`,
  },
  {
    app: 'propertycgi',
    feature: '4k',
    creditCost: PROPERTYCGI_CREDIT_COST_4K,
    label: 'PropertyCGI 4K',
    buttonLabel: `Generate — ${PROPERTYCGI_CREDIT_COST_4K} credits`,
  },
  {
    app: 'propertycgi',
    feature: 'selective_edit',
    creditCost: PROPERTYCGI_CREDIT_COST_EDIT,
    label: 'PropertyCGI selective edit',
    buttonLabel: `Edit — ${PROPERTYCGI_CREDIT_COST_EDIT} credits`,
  },
  {
    app: 'videomotion',
    feature: 'script',
    creditCost: VIDEOMOTION_CREDITS_FOR_SCRIPT,
    label: 'VideoMotion script',
    buttonLabel: `Script — ${formatVideomotionCredits(VIDEOMOTION_CREDITS_FOR_SCRIPT)}`,
  },
  {
    app: 'videomotion',
    feature: 'analysis',
    creditCost: VIDEOMOTION_CREDITS_FOR_ANALYSIS,
    label: 'VideoMotion analysis',
    buttonLabel: 'Analyse — free',
  },
  {
    app: 'videomotion',
    feature: 'export',
    creditCost: VIDEOMOTION_CREDITS_FOR_EXPORT,
    label: 'VideoMotion export',
    buttonLabel: `Export — ${formatVideomotionCredits(VIDEOMOTION_CREDITS_FOR_EXPORT)}`,
  },
  {
    app: 'propertycut',
    feature: 'imagemotion_scene',
    creditCost: PROPERTYCUT_IMAGEMOTION_SCENE_CREDITS,
    label: 'PropertyCuts · ImageMotion scene (5 seconds)',
    buttonLabel: `Animate with ImageMotion — ${PROPERTYCUT_IMAGEMOTION_SCENE_CREDITS} credits`,
  },
  {
    app: 'propertycut',
    feature: 'videomotion_walkthrough',
    creditCost: PROPERTYCUT_VIDEOMOTION_WALKTHROUGH_CREDITS,
    label: `PropertyCuts · VideoMotion walkthrough (up to ${VIDEOMOTION_MAX_VIDEOS_PER_PROJECT} clips)`,
    buttonLabel: `Apply VideoMotion — ${formatVideomotionCredits(PROPERTYCUT_VIDEOMOTION_WALKTHROUGH_CREDITS)}`,
  },
  {
    app: 'brochures',
    feature: 'generate',
    creditCost: BROCHURE_GENERATE_CREDITS,
    label: 'Branded brochure PDF',
    buttonLabel: 'Download branded PDF — free',
  },
  {
    app: 'brochures',
    feature: 'unbranded_export',
    creditCost: BROCHURE_UNBRANDED_CREDITS,
    label: 'Unbranded brochure PDF',
    buttonLabel: `Remove branding — ${BROCHURE_UNBRANDED_CREDITS} credit`,
  },
  {
    app: 'brochures',
    feature: 'ai_style',
    creditCost: BROCHURE_AI_STYLE_CREDITS,
    label: 'AI brochure style',
    buttonLabel: `Create an AI style — ${BROCHURE_AI_STYLE_CREDITS} credits`,
  },
  {
    app: 'brochures',
    feature: 'marketing_pack',
    creditCost: BROCHURE_MARKETING_PACK_CREDITS,
    label: 'Brochure marketing pack',
    buttonLabel: `Marketing pack — ${BROCHURE_MARKETING_PACK_CREDITS} credits`,
  },
  {
    app: 'tours',
    feature: 'floor_plan_order',
    creditCost: TOURS_FLOOR_PLAN_ORDER_CREDITS,
    label: '360 Tours floor plan order',
    buttonLabel: `Order — ${TOURS_FLOOR_PLAN_ORDER_CREDITS} credits`,
  },
  {
    app: 'tours',
    feature: 'pano_edit',
    creditCost: TOURS_PANO_EDIT_CREDITS,
    label: '360 Tours panorama edit',
    buttonLabel: `Edit — ${TOURS_PANO_EDIT_CREDITS} credit`,
  },
  {
    app: 'hioz',
    feature: 'guide_per_minute',
    creditCost: HIOZ_CREDIT_COSTS.guidePerMinute,
    label: 'HIOZ guide (per minute)',
    buttonLabel: `Guide — ${HIOZ_CREDIT_COSTS.guidePerMinute} credit/min`,
  },
  {
    app: 'hioz',
    feature: 'translation_per_language',
    creditCost: HIOZ_CREDIT_COSTS.translationPerLanguage,
    label: 'HIOZ translation (per language)',
    buttonLabel: `Translate — ${HIOZ_CREDIT_COSTS.translationPerLanguage} credit`,
  },
];

const seenPhotoclearFeatures = new Set(ACTIONS.filter((row) => row.app === 'photoclear').map((row) => row.feature));
for (const action of PHOTOCLEAR_OPERATIONS) {
  if (seenPhotoclearFeatures.has(action.feature)) continue;
  seenPhotoclearFeatures.add(action.feature);
  ACTIONS.push({
    app: 'photoclear',
    feature: action.feature,
    creditCost: action.creditCost,
    label: `PhotoClear / ${action.label}`,
    buttonLabel: `${action.label} — ${formatPhotoclearCreditLabel(action.creditCost)}`,
  });
}

for (const plan of HOSTING_CREDIT_COSTS) {
  ACTIONS.push({
    app: 'hosting',
    feature: plan.feature,
    creditCost: plan.credits,
    label: `Hosting ${plan.label}`,
    buttonLabel: `Publish — ${plan.credits} credits`,
  });
}

ACTIONS.push(
  {
    app: 'seo',
    feature: 'backlink_report',
    creditCost: SEO_ACTION_CREDITS.backlink_report,
    label: 'ReHub SEO / backlink opportunities report',
    buttonLabel: 'Find backlink opportunities — 5 credits',
  },
  {
    app: 'seo',
    feature: 'competitor_analysis',
    creditCost: SEO_ACTION_CREDITS.competitor_analysis,
    label: 'ReHub SEO / competitor analysis',
    buttonLabel: 'Compare competitors — 5 credits',
  },
);

for (const plan of SHOWCASE_HOSTING_CREDIT_COSTS) {
  ACTIONS.push({
    app: 'showcase',
    feature: plan.feature,
    creditCost: plan.credits,
    label: `Showcase hosting ${plan.label}`,
    buttonLabel: `Publish — ${plan.credits} credits`,
  });
}

export const REHUB_CREDIT_ACTIONS: readonly CreditActionDefinition[] = ACTIONS;

const ACTION_INDEX = new Map<string, CreditActionDefinition>();
for (const action of ACTIONS) {
  ACTION_INDEX.set(`${action.app}:${action.feature}`, action);
}

export function getCreditAction(key: CreditActionKey): CreditActionDefinition | null {
  return ACTION_INDEX.get(`${key.app}:${key.feature}`) ?? null;
}

export function getCreditCost(key: CreditActionKey, fallback = 1): number {
  return getCreditAction(key)?.creditCost ?? fallback;
}

export function getCreditButtonLabel(key: CreditActionKey, quantity = 1): string {
  const action = getCreditAction(key);
  const unit = action?.creditCost ?? 1;
  const total = unit * Math.max(1, quantity);
  const noun = total === 1 ? 'credit' : 'credits';
  if (quantity > 1) {
    return `${action?.label || 'Generate'} ${quantity} — ${total} ${noun}`;
  }
  return action?.buttonLabel || `Generate — ${total} ${noun}`;
}

export function formatGenerationCostLabel(creditCost: number, verb = 'Generate', quantity = 1): string {
  const total = creditCost * Math.max(1, quantity);
  const noun = total === 1 ? 'credit' : 'credits';
  if (quantity > 1) return `${verb} ${quantity} — ${total} ${noun}`;
  return `${verb} — ${total} ${noun}`;
}

export const PHOTO_CLEAR_CREDIT_COST = getCreditCost({ app: 'photoclear', feature: 'declutter_light' });
export const STAGING_STANDARD_CREDIT_COST = getCreditCost({ app: 'staging', feature: 'standard' });
export const STAGING_PREMIUM_CREDIT_COST = getCreditCost({ app: 'staging', feature: 'premium' });
export const STAGING_FULL_RENOVATION_CREDIT_COST = getCreditCost({ app: 'staging', feature: 'full_renovation' });
export const HYPERDUSK_DAY_TO_DUSK_CREDIT_COST = getCreditCost({ app: 'hyperdusk', feature: 'day_to_dusk' });
export const IMAGEMOTION_QUICK_CREDIT_COST = getCreditCost({ app: 'imagemotion', feature: 'quick' });
export const IMAGEMOTION_STANDARD_CREDIT_COST = getCreditCost({ app: 'imagemotion', feature: 'standard' });
export const IMAGEMOTION_PRO_CREDIT_COST = getCreditCost({ app: 'imagemotion', feature: 'pro' });
export const IMAGEMOTION_ULTRA_CREDIT_COST = getCreditCost({ app: 'imagemotion', feature: 'ultra' });
export const PHOTOEDIT_ENHANCEMENT_CREDIT_COST = getCreditCost({
  app: 'photoedit',
  feature: 'standard_enhancement',
});
export const FLOORPLANS_AI_CONVERSION_CREDIT_COST = getCreditCost({
  app: 'floorplans-studio',
  feature: 'ai_conversion',
});
